"use client";

import { useEffect, useState, useCallback } from "react";
import { getWithTokenAPI } from "../../utils/Api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  CircularProgress,
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Link,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";

const Revenuemanagement = () => {
  const [paymentRecords, setPaymentRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedRecordDetails, setSelectedRecordDetails] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const primaryGreen = "#DC3545";
  const lightGreen = "#ffe5e5";
  const secondaryTextColor = "rgba(0, 0, 0, 0.6)";

  const categories = ["all", "drone", "soil", "farm", "order", "subscription"];

  const fetchPaymentSummary = useCallback(async (category) => {
    const token = localStorage.getItem("token");
    setLoading(true);
    try {
      const endpoint =
        category === "all"
          ? `/report/payment-summary/all`
          : `/report/payment-summary/${category}`;
      const res = await getWithTokenAPI(token, endpoint);

      if (res.statusCode === 200) {
        setPaymentRecords(res.data || []);
      } else {
        toast.error(res.message || "Failed to load payment summary data.");
        setPaymentRecords([]);
      }
    } catch (error) {
      console.error("Fetch payment summary error:", error);
      toast.error("Failed to load payment summary due to a network error.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCategoryChange = (event) => {
    const newCategory = event.target.value;
    setSelectedCategory(newCategory);
    setCurrentPage(1);
    fetchPaymentSummary(newCategory);
  };

  const getPaymentStatus = (record) => {
    if (record.source === "Subscription") {
      return record.paid_status || "N/A";
    }
    return record.payment_status || record.cart_payment_status || "N/A";
  };

  const handleViewDetails = (record) => {
    setSelectedRecordDetails(record);
    setOpenDetailsDialog(true);
  };

  const handleCloseDetailsDialog = () => {
    setOpenDetailsDialog(false);
    setSelectedRecordDetails(null);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Pagination
  const totalPages = Math.ceil(paymentRecords.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = paymentRecords.slice(indexOfFirstItem, indexOfLastItem);

  useEffect(() => {
    fetchPaymentSummary(selectedCategory);
  }, [fetchPaymentSummary, selectedCategory]);

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <ToastContainer position="top-center" autoClose={1500} />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 1,
          p: 2,
          backgroundColor: lightGreen,
          borderRadius: 2,
          borderLeft: `5px solid ${primaryGreen}`,
        }}
      >
        <AnalyticsIcon sx={{ color: primaryGreen, fontSize: 50, mr: 2 }} />
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ color: primaryGreen, fontWeight: "bold" }}
          >
            Revenue
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            View and filter all payment transactions
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h5"
          component="h2"
          sx={{ color: primaryGreen, fontWeight: "bold" }}
        >
          Payment Details
        </Typography>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="category-select-label">Filter by Category</InputLabel>
          <Select
            labelId="category-select-label"
            id="category-select"
            value={selectedCategory}
            label="Filter by Category"
            onChange={handleCategoryChange}
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: primaryGreen,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: primaryGreen,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: primaryGreen,
              },
              "& .MuiSelect-icon": {
                color: primaryGreen,
              },
              color: primaryGreen,
            }}
          >
            {categories.map((category) => (
              <MenuItem key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}{" "}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mt: 2 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <CircularProgress style={{ color: "#DC3545" }} />
            <Typography sx={{ mt: 2, color: "#DC3545" }}>
              Loading payment records...
            </Typography>
          </Box>
        ) : paymentRecords.length > 0 ? (
          <>
            <TableContainer
              component={Paper}
              sx={{ border: "1px solid #e0e0e0" }}
            >
              <Table sx={{ minWidth: 650 }} aria-label="payment records table">
                <TableHead sx={{ backgroundColor: lightGreen }}>
                  <TableRow>
                    <TableCell
                      sx={{ color: secondaryTextColor, fontWeight: "bold" }}
                    >
                      S.No
                    </TableCell>
                    {selectedCategory !== "subscription" ? (
                      <>
                        <TableCell
                          sx={{ color: secondaryTextColor, fontWeight: "bold" }}
                        >
                          Product Name
                        </TableCell>
                        <TableCell
                          sx={{ color: secondaryTextColor, fontWeight: "bold" }}
                        >
                          Source/Category
                        </TableCell>
                      </>
                    ) : (
                      <TableCell
                        sx={{ color: secondaryTextColor, fontWeight: "bold" }}
                      >
                        User
                      </TableCell>
                    )}
                    <TableCell
                      sx={{ color: secondaryTextColor, fontWeight: "bold" }}
                    >
                      Total Amount(Ksh)
                    </TableCell>
                    <TableCell
                      sx={{ color: secondaryTextColor, fontWeight: "bold" }}
                    >
                      Tax Amount(Ksh)
                    </TableCell>
                    <TableCell
                      sx={{ color: secondaryTextColor, fontWeight: "bold" }}
                    >
                      Payment Status
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: "bold", color: secondaryTextColor }}
                      align="center"
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentItems.map((record, index) => {
                    const uniqueKey = `${record.source}-${
                      record.payment_reference ||
                      record.id ||
                      record.precision_id ||
                      record.subscription_id
                    }-${indexOfFirstItem + index}`;

                    return (
                      <TableRow
                        key={uniqueKey}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {indexOfFirstItem + index + 1}
                        </TableCell>
                        {selectedCategory !== "subscription" ? (
                          <>
                            <TableCell>
                              <Link
                                component="button"
                                variant="body2"
                                onClick={() => handleViewDetails(record)}
                                sx={{
                                  color: primaryGreen,
                                  textAlign: "left",
                                  textDecoration: "none",
                                  fontWeight: "bold",
                                  "&:hover": { color: primaryGreen },
                                }}
                              >
                                {record.name || "N/A"}
                              </Link>
                            </TableCell>
                            <TableCell>
                              {record.source === "ProductOrder"
                                ? "Order"
                                : record.source === "Subscription"
                                ? "Subscription"
                                : record.category || "N/A"}
                            </TableCell>
                          </>
                        ) : (
                          <TableCell>
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => handleViewDetails(record)}
                              sx={{
                                color: primaryGreen,
                                textAlign: "left",
                                textDecoration: "none",
                                fontWeight: "bold",
                                "&:hover": { color: primaryGreen },
                              }}
                            >
                              {record.user_id || record.name || "N/A"}
                            </Link>
                          </TableCell>
                        )}
                        <TableCell>
                          {record.total_amount?.toFixed(2) || "0.00"}
                        </TableCell>
                        <TableCell>
                          {record.tax_amount?.toFixed(2) || "0.00"}
                        </TableCell>
                        <TableCell>{getPaymentStatus(record)}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            sx={{ color: primaryGreen, mr: 1 }}
                            aria-label="view order details"
                            onClick={() => handleViewDetails(record)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "end", mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  sx={{
                    "& .Mui-selected": {
                      backgroundColor: `${primaryGreen} !important`,
                      color: "white !important",
                    },
                    "& .MuiPaginationItem-root": {
                      borderColor: primaryGreen,
                      color: primaryGreen,
                    },
                  }}
                />
              </Box>
            )}
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "150px",
              border: "1px dashed #e0e0e0",
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              No payment records found for the selected category.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Details Dialog */}
      <Dialog
        open={openDetailsDialog}
        onClose={handleCloseDetailsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: primaryGreen,
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 2,
            px: 3,
          }}
        >
          <Typography variant="h6" component="div">
            Payment Record Details
          </Typography>
          <IconButton
            onClick={handleCloseDetailsDialog}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedRecordDetails && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 4,
              }}
            >
              <Box>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: "bold", mb: 1 }}
                >
                  Basic Information:
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "max-content 1fr",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: "bold", width: "140px" }}
                  >
                    Source:{" "}
                  </Typography>
                  <Typography variant="body1">
                    {selectedRecordDetails.source || "N/A"}
                  </Typography>

                  {selectedRecordDetails.source === "PrecisionManagement" && (
                    <>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: "bold", width: "140px" }}
                      >
                        Category:{" "}
                      </Typography>
                      <Typography variant="body1">
                        {selectedRecordDetails.category || "N/A"}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{ fontWeight: "bold", width: "140px" }}
                      >
                        Service:{" "}
                      </Typography>
                      <Typography variant="body1">
                        {selectedRecordDetails.service || "N/A"}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{ fontWeight: "bold", width: "140px" }}
                      >
                        Spraying Date:{" "}
                      </Typography>
                      <Typography variant="body1">
                        {selectedRecordDetails.sprayingdate
                          ? new Date(
                              selectedRecordDetails.sprayingdate
                            ).toLocaleDateString()
                          : "N/A"}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{ fontWeight: "bold", width: "140px" }}
                      >
                        Area:{" "}
                      </Typography>
                      <Typography variant="body1">
                        {selectedRecordDetails.area?.toFixed(2) || "N/A"}
                      </Typography>
                    </>
                  )}

                  {selectedRecordDetails.source === "ProductOrder" && (
                    <>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: "bold", width: "140px" }}
                      >
                        Order ID:{" "}
                      </Typography>
                      <Typography variant="body1">
                        {selectedRecordDetails.order_id || "N/A"}
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{ fontWeight: "bold", width: "140px" }}
                      >
                        Quantity:{" "}
                      </Typography>
                      <Typography variant="body1">
                        {selectedRecordDetails.quantity || "N/A"}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
              <Box>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: "bold", mb: 1 }}
                >
                  Payment Information:
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "max-content 1fr",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: "bold", width: "140px" }}
                  >
                    Name:{" "}
                  </Typography>
                  <Typography variant="body1">
                    {selectedRecordDetails.name || "N/A"}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{ fontWeight: "bold", width: "140px" }}
                  >
                    Total Amount(Ksh):
                  </Typography>
                  <Typography variant="body1">
                    {selectedRecordDetails.total_amount?.toFixed(2) || "0.00"}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{ fontWeight: "bold", width: "140px" }}
                  >
                    Tax Amount(Ksh):
                  </Typography>
                  <Typography variant="body1">
                    {selectedRecordDetails.tax_amount?.toFixed(2) || "0.00"}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{ fontWeight: "bold", width: "140px" }}
                  >
                    Payment Reference:{" "}
                  </Typography>
                  <Typography variant="body1">
                    {selectedRecordDetails.payment_reference || "N/A"}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{ fontWeight: "bold", width: "140px" }}
                  >
                    Payment Status:{" "}
                  </Typography>
                  <Typography variant="body1">
                    {getPaymentStatus(selectedRecordDetails)}
                  </Typography>

                  {selectedRecordDetails.user_id && (
                    <>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: "bold", width: "140px" }}
                      >
                        User ID:{" "}
                      </Typography>
                      <Typography variant="body1">
                        {selectedRecordDetails.user_id || "N/A"}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDetailsDialog}
            variant="contained"
            sx={{
              backgroundColor: primaryGreen,
              color: "white",
              "&:hover": {
                backgroundColor: "#8fb82d",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Revenuemanagement;
